import { PlacaNoCartaz } from './TrancaDeOrdem.js';
import { useT } from '../lib/idioma.js';

/**
 * ── O CURSO QUE NÃO É SEU, DITO ANTES DO CLIQUE (02/10/2026) ────────────────
 *
 * Ordem do dono, no dia:
 *
 * > *«quando um aluno está em um grupo de acesso e entra na vitrine, aparecem
 * > diversos cursos aos quais ele não tem acesso … mas as capas não mostram que
 * > ele não está liberado, deveria ficar de outra cor»*
 *
 * **Medido em produção no mesmo dia**, na única área com grupos: 9 grupos, 21
 * cursos publicados, 19 deles com página de venda declarada. Quem está no grupo
 * «Openclaw Vitalício» (93 pessoas) tem **2** — e as outras 19 capas chegavam
 * a cores cheias, do mesmo tamanho, com o mesmo cartaz e o mesmo levantar do
 * rato das duas que são dela. O único sinal era uma linha de 12px debaixo do
 * título, e uma linha de 12px não se lê a três metros de distância.
 *
 * ## O que esta peça é, e o que ela recusa ser
 *
 * **Ela é o carimbo, e só ele.** O preto e branco mora em `.vst-arte-sem-acesso`
 * (`index.css`), o véu e o glifo do rato em `VeuDoCartaz`, e a frase debaixo do
 * título continua onde o dono a pôs em 18/09 — *Você ainda não tem*, em
 * `--destaque`. Esta peça não refaz nenhuma das três: ela acrescenta a palavra
 * que diz **qual** das quatro espécies de cartaz fechado é esta.
 *
 * **Ela NÃO é um botão.** É estado: «não incluído» descreve o que o cartaz é
 * para esta pessoa. O gesto está no cartaz inteiro, que é um link para a página
 * de venda — e um segundo alvo dentro do primeiro seria um clique que cai no
 * sítio errado por acidente.
 *
 * ## Por que «não incluído», e não «comprar»
 *
 * Porque o que a casa não sabe é como a pessoa chegou ao que tem: compra,
 * voucher, importação em lote, matrícula pela mão do tutor. «Comprar» afirma um
 * gesto e um preço que esta tela não conhece; «não incluído» afirma só o que é
 * verdade em todos os casos — este curso não está no acesso dela. A palavra que
 * vende é a da página de venda, que é do tutor, e é para lá que o clique vai.
 */
export function SeloSemAcesso() {
  const t = useT();
  return (
    <PlacaNoCartaz
      data-vst-sem-acesso="selo"
      /* A única das quatro chapas que não é `--fundo`: ver `PlacaNoCartaz`. O
         par `--destaque`/`--sobre-destaque` é o mesmo dos botões da área, e é
         derivado por `textoSobre` no core — a tinta legível sobre a cor que o
         tutor escolheu, medida e não suposta. */
      chapa="var(--destaque)"
      tinta="var(--sobre-destaque)"
    >
      <Etiqueta />
      {t('vitrine.naoIncluido')}
    </PlacaNoCartaz>
  );
}

/**
 * ── O CARIMBO DA AULA DE AMOSTRA (02/10/2026) ───────────────────────────────
 *
 * A outra ponta do mesmo achado do dono, no eixo da AULA. `lesson.free_preview`
 * põe uma aula solta na prateleira de quem **não tem o curso** — e até
 * `73a10fd2c` ela aparecia a cores cheias, sem carimbo, e o clique recusava.
 * Desde aquele commit ela abre de verdade; faltava a tela dizer **o que ela é**.
 *
 * ## Por que esta NÃO leva véu nem preto e branco
 *
 * Porque o véu é a linguagem de «isto não abre», e esta aula abre. Pôr-lhe a
 * mesma roupa do curso sem acesso seria desconvidar o único clique da
 * prateleira que o servidor acabou de autorizar — e ensinaria o olho que o
 * preto e branco às vezes quer dizer «podes entrar», que é como uma linguagem
 * visual deixa de informar.
 *
 * ## «Amostra», e não «Não incluído»
 *
 * São fatos diferentes e nenhum dos dois é o outro: *não incluído* diz «isto
 * não é teu»; *amostra* diz «esta parte é, e o resto não». A chapa é a mesma
 * `--destaque` porque as duas pertencem à mesma família — a conversa sobre o
 * que ainda se pode ter —, e a palavra é que as separa de perto.
 */
export function SeloDeAmostra() {
  const t = useT();
  return (
    <PlacaNoCartaz data-vst-amostra="selo" chapa="var(--destaque)" tinta="var(--sobre-destaque)">
      <Etiqueta />
      {t('vitrine.amostra')}
    </PlacaNoCartaz>
  );
}

/**
 * A etiqueta de preço, desenhada — e nunca um emoji, pela razão do `Cadeado`:
 * emoji muda de forma, de peso e de cor em cada sistema, e este símbolo tem de
 * parecer o mesmo no carimbo e no véu.
 *
 * `currentColor` para herdar a tinta de onde estiver, e `aria-hidden` porque a
 * palavra ao lado já diz tudo por extenso.
 */
export function Etiqueta({ tamanho = 12 }: { tamanho?: number }) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M6.3 1.5H10.5V5.7L5.4 10.8 1.2 6.6z" />
      <circle cx="8.4" cy="3.6" r="0.75" />
    </svg>
  );
}

/**
 * O glifo que o rato revela no cartaz sem acesso — a seta que SAI.
 *
 * No lugar do cadeado do véu, e a diferença é o fato: o cadeado responde «isto
 * não abre», e aqui abre — noutro endereço, que é o da página de venda do
 * tutor. A seta diagonal é a marca universal de «este link sai daqui», e é a
 * mesma coisa que o `target="_blank"` do cartaz faz.
 */
export function SetaQueSai({ tamanho = 18 }: { tamanho?: number }) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M6.5 11.5 11.5 6.5" />
      <path d="M7 6.5h4.5V11" />
    </svg>
  );
}
